import type { IAnyModelType, Instance, IStateTreeNode, SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { getRoot, getSnapshot, types } from "@gadgetinc/mobx-quick-tree";
import { find, groupBy, head, isEqual, orderBy, pick, sortBy, sumBy, unionBy, uniqWith } from "lodash";
import type { AllApiKeys, AllApiKeysUnion, ApiKey } from "./ApiKey";
import { LegacyApiKey } from "./ApiKey";
import type { AppSettings } from "./AppSettings";
import type { AuthenticationMethod } from "./auth/AuthenticationMethod";
import type { SourceFile } from "./code/SourceFile";
import {
  AllConfigurationVariables,
  AllConfigurationVariableUnion,
  ConfigurationVariable,
  LegacyConfigurationVariable,
} from "./ConfigurationVariable";
import type { Connection } from "./connections/Connection";
import type { ShopifyConnectionConfig } from "./connections/ShopifyConnectionConfig";
import { ShopifyMetafieldFieldContributorID } from "./contributors/shopify/ShopifyFieldContributor";
import type { GlobalAction } from "./database/behaviour/GlobalAction";
import type { DataModel } from "./database/DataModel";
import { type LegacyEnvironmentName } from "./Environment";
import { getEnvironmentIndex } from "./getEnvironmentIndex";
import type {
  AppSettingsIndexEntry,
  AuthenticationMethodIndexEntry,
  ConnectionIndexEntry,
  GlobalActionIndexEntry,
  ModelIndexEntry,
  ProblemsIndexEntry,
  ReferenceNode,
  RoleIndexEntry,
  ShopifyConnectionIndexEntry,
  SourceFileIndexEntry,
} from "./IndexEntry";
import type { LiveTreeMap } from "./live-tree/LiveTree";
import type { LiveRoot } from "./LiveRoot";
import { ProblemSeverity, problemSeverityIsErrorOrAbove } from "./problems/ProblemSeverity";
import type { ProblemIdentifier, ProblemMessage, ProblemsWithReferenceNode } from "./problems/ProblemsList";
import type { Role } from "./Role";
import type { FieldType } from "./type-system/FieldType";
import type { IAnyModelOrUnionType } from "./utils";
import { gadgetModelType, getAppSettings } from "./utils";
import { WorkspacePageType } from "./WorkspacePageType";
import path from "path";
import { defaultSettingsMetadataFilePath } from "./settings/file-based-actions/constants";
import { FilesystemIntrospector } from "./FilesystemIntrospector";

export type GroupedProblemIndexEntry = Readonly<{
  problem: SnapshotIn<typeof ProblemMessage>;
  node: ReferenceNode;
  subNodeKey?: string;
  nodeLabels?: ProblemIdentifier[];
}>;

export type DeployIssue = Readonly<{
  severity: ProblemSeverity;
  message: string;
  node?: ReferenceNode;
  nodeLabels?: ProblemIdentifier[];
}>;

export type IndexWithProblems =
  | ModelIndexEntry
  | SourceFileIndexEntry
  | RoleIndexEntry
  | GlobalActionIndexEntry
  | ConfigurationVariableIndexEntry
  | ApiKeyIndexEntry
  | ConnectionIndexEntry
  | AuthenticationMethodIndexEntry
  | AppSettingsIndexEntry;

export type IndexedProblemsList = Record<string, GroupedProblemIndexEntry[]>;

export interface SummarizedProblem extends ProblemsIndexEntry {
  groupKey: string;
  tabType: WorkspacePageType;
  tabPriority: number;
  nodeNamePriority: string;
  title: string;
  displayPath: string | undefined;
  filePath: string;
}

export type IndexEntryKey =
  | "models"
  | "roles"
  | "files"
  | "globalActions"
  | "configurationVariables"
  | "authenticationMethods"
  | "apiKeys"
  | "connections"
  | "appSettings";

export type GetProblems = {
  sources?: IndexEntryKey[];
  sourcesForErrorCount?: IndexEntryKey[];
  sourcesForWarningCount?: IndexEntryKey[];
  includeErrors?: boolean;
  includeWarnings?: boolean;
};

export type SourceFileReference = Readonly<
  | { model: ModelSourceFileReference }
  | { globalAction: ActionSourceFileReference }
  | { role: RoleSourceFileReference }
  | { route: RouteSourceFileReference }
>;

export type ModelSourceFileReference = Readonly<
  {
    key: string;
    apiIdentifier: string;
    color: string;
  } & ({ field: FieldSourceFileReference } | { action: ActionSourceFileReference })
>;

export type FieldSourceFileReference = Readonly<
  {
    key: string;
    name: string;
    apiIdentifier: string;
  } & ({ validation: ValidationSourceFileReference } | { fieldType: FieldType.Computed; configuration: Readonly<{ key: string }> })
>;

export type ActionSourceFileReference = Readonly<
  {
    key: string;
    apiIdentifier: string;
    virtualAction: boolean;
  } & (
    | { actionAsCode: ActionAsCodeSourceFileReference }
    | { precondition: PreconditionSourceFileReference }
    | { effect: EffectSourceFileReference }
  )
>;

export type ValidationSourceFileReference = Readonly<{
  key: string;
}>;

export type PreconditionSourceFileReference = Readonly<{
  key: string;
}>;

export type ActionAsCodeSourceFileReference = Readonly<{
  key: string;
}>;

export type EffectSourceFileReference = Readonly<{
  key: string;
}>;

export type RoleSourceFileReference = Readonly<{
  key: string;
  name: string;
  grant: GrantSourceFileReference;
}>;

export type GrantSourceFileReference = Readonly<{
  key: string;
  name: string;
  filter: ModelFilterSourceFileReference;
}>;

export type ModelFilterSourceFileReference = Readonly<{
  key: string;
}>;

export type RouteSourceFileReference = Readonly<{
  environment: LegacyEnvironmentName;
  url: string;
}>;

const summarizeProblem = (problem: ProblemsIndexEntry, settings: AppSettings, tabType: WorkspacePageType): SummarizedProblem => {
  let groupKey: string = tabType;
  let tabPriority: number = 9;
  let title: string = "Unknown";
  let displayPath: string | undefined = undefined;
  let filePath: string = "unknown";
  const nodeNamePriority: string = problem.node.parentApiIdentifier ?? problem.node.name ?? problem.node.apiIdentifier ?? problem.node.type;

  if (tabType == WorkspacePageType.AppSettingIndex) {
    tabPriority = 1;
    groupKey = `${tabType}-${problem.node.key}`;
    title = "General";
    displayPath = "settings";
    filePath = settings.fileBasedActions ? defaultSettingsMetadataFilePath : "General - settings";
  } else if (tabType == WorkspacePageType.AuthSettingsIndex) {
    tabPriority = 1;
    groupKey = `${tabType}-${problem.node.key}`;
    title = "Auth";
    displayPath = "settings";
    filePath = settings.fileBasedActions ? defaultSettingsMetadataFilePath : "Auth - settings";
  } else if (tabType == WorkspacePageType.Model) {
    tabPriority = 2;
    groupKey = `${tabType}-${problem.node.parentKey ?? problem.node.key}`;
    title = "schema";
    const modelApiIdentifier = problem.node.parentApiIdentifier ?? problem.node.apiIdentifier ?? "unknown";
    displayPath = path.join(settings.pathPreferences.modelsFolderPath, modelApiIdentifier);
    filePath = settings.fileBasedActions
      ? `${settings.pathPreferences.modelsFolderPath}${modelApiIdentifier}/schema.gadget.ts`
      : `${modelApiIdentifier} - Model`;
  } else if (tabType == WorkspacePageType.GlobalActions) {
    tabPriority = 3;
    title = problem.node.apiIdentifier ?? "unknown";
    filePath = `${problem.node.apiIdentifier} - Global action`;
  } else if (tabType == WorkspacePageType.Code) {
    tabPriority = 4;
    filePath = problem.messages[0].sourceLocation?.filePath ?? "unknown";
    groupKey = `${tabType}-${filePath}`;

    if (settings.fileBasedActions) {
      const parts = filePath.split("/");
      title = parts[parts.length - 1];
      displayPath = parts.slice(0, -1).join("/");
    } else {
      title = filePath;
    }
  } else if (tabType == WorkspacePageType.ConfigurationVariablesIndex) {
    tabPriority = 5;
    title = "Environment variables";
    displayPath = "settings";
    filePath = "Environment variables - settings";
  } else if (tabType == WorkspacePageType.PermissionsIndex) {
    tabPriority = 6;
    if (settings.fileBasedActions) {
      title = "permissions";
      displayPath = "accessControl";
      filePath = `accessControl/permissions.gadget.ts`;
    } else {
      title = "Access control";
      filePath = "Access control";
    }
  } else if (
    tabType == WorkspacePageType.ShopifyConnectionsIndex ||
    tabType == WorkspacePageType.ShopifyConnectionsAPIUpgrade ||
    tabType == WorkspacePageType.ShopifyInstalls
  ) {
    tabPriority = 7;
    title = "Shopify";
    displayPath = "settings";
    filePath = settings.fileBasedActions ? defaultSettingsMetadataFilePath : "Shopify - settings";
  } else if (tabType == WorkspacePageType.GoogleOauthSettingsIndex) {
    tabPriority = 7;
    title = "Google OAuth";
    displayPath = "settings";
    filePath = settings.fileBasedActions ? defaultSettingsMetadataFilePath : "Google OAuth - settings";
  } else if (tabType == WorkspacePageType.OpenAIConnectionIndex) {
    tabPriority = 7;
    title = "OpenAI";
    displayPath = "settings";
    filePath = settings.fileBasedActions ? defaultSettingsMetadataFilePath : "OpenAI - settings";
  } else if (tabType == WorkspacePageType.SentryConnectionIndex) {
    tabPriority = 7;
    title = "Sentry";
    displayPath = "settings";
    filePath = settings.fileBasedActions ? defaultSettingsMetadataFilePath : "Sentry - settings";
  } else if (tabType == WorkspacePageType.ApiKeys) {
    tabPriority = 8;
    title = "API keys";
    displayPath = "settings";
    filePath = "API keys - settings";
  }

  return {
    ...problem,
    tabType,
    groupKey,
    tabPriority,
    nodeNamePriority,
    title,
    displayPath,
    filePath,
  };
};

export function sourceFileReferenceKey(reference: SourceFileReference) {
  if ("model" in reference) {
    if ("field" in reference.model) {
      if ("validation" in reference.model.field) {
        return reference.model.field.validation.key;
      } else {
        return reference.model.field.configuration.key;
      }
    } else {
      if ("actionAsCode" in reference.model.action) {
        return reference.model.action.actionAsCode.key;
      } else {
        if ("precondition" in reference.model.action) {
          return reference.model.action.precondition.key;
        } else {
          return reference.model.action.effect.key;
        }
      }
    }
  } else if ("globalAction" in reference) {
    return reference.globalAction.key;
  } else if ("role" in reference) {
    return reference.role.grant.filter.key;
  } else {
    return reference.route.url;
  }
}

export type ConfigurationVariableIndexEntry = Readonly<
  | {
      type: "ConfigurationVariableIndexEntry";
      apiIdentifier: string;
      problems: ProblemsIndexEntry[];
      createdDate: number;
      key: string;
      values: Record<"Development" | "Production", { isSecret: boolean; value: string | null }>;
    }
  | {
      type: "EnvironmentConfigurationVariableIndexEntry";
      apiIdentifier: string;
      problems: ProblemsIndexEntry[];
      createdDate: number;
      key: string;
      value: string | null;
      isSecret: boolean;
    }
>;

export type LegacyApiKeyIndexEntry = {
  type: "ApiKeyIndexEntry" | "ApiKey";
  name: string;
  roles: string[];
  problems: ProblemsIndexEntry[];
  createdDate: number;
  key: string;
  secretKeys: Record<"Development" | "Production", { secretKey: string }>;
};

export type ApiKeyIndexEntry = Readonly<
  | LegacyApiKeyIndexEntry
  | {
      type: "EnvironmentApiKeyIndexEntry";
      name: string;
      roles: string[];
      problems: ProblemsIndexEntry[];
      createdDate: number;
      key: string;
      secretKey: string;
    }
>;

export const buildModelIndex = (model: DataModel, indexProblems = true): ModelIndexEntry => {
  return {
    type: "ModelIndexEntry" as const,
    ...pick(model, ["key", "name", "color", "apiIdentifier"]),
    fields: model.fieldsAsArray.map((field) => ({
      type: "FieldIndexEntry",
      ...pick(field, ["key", "apiIdentifier", "fieldType"]),
      contributorConfiguration: pick(field.contributorConfiguration, [ShopifyMetafieldFieldContributorID]),
      configuration: pick(field.configuration, [
        "key",
        "inverseFieldKey",
        "inverseJoinModelFieldKey",
        "inverseRelatedModelFieldKey",
        "includeTime",
        "sortable",
      ]),
    })),
    actions: model.actions.map((action) => ({
      type: "ActionIndexEntry",
      ...pick(action, ["key", "apiIdentifier"]),
    })),
    problems: indexProblems
      ? model.allProblems.map((problem: ProblemsWithReferenceNode) => {
          return {
            messages: getSnapshot(problem.messages),
            node: {
              ...pick(problem.node, ["type", "key", "apiIdentifier", "fieldType"]),
              parentKey: model.key,
              parentApiIdentifier: model.apiIdentifier,
            },
            subNodeKey: problem.subNodeKey,
            nodeLabels: problem.nodeLabels,
          };
        })
      : [],
    supportsConditionalPermissions: model.storageStrategy.supportsConditionalPermissions,
    supportedFieldTypes: model.storageStrategy.supportedFieldTypes,
    createdDate: model.createdDate.getTime(),
  };
};

export const buildRoleIndex = (role: Role, indexProblems = true): RoleIndexEntry => {
  return {
    type: "RoleIndexEntry" as const,
    ...pick(role, ["key", "name"]),
    problems: indexProblems
      ? role.allProblems.map((problem) => {
          return {
            messages: getSnapshot(problem.messages),
            node: pick(problem.node, ["type", "key", "name"]),
            subNodeKey: problem.subNodeKey,
            nodeLabels: problem.nodeLabels,
          };
        })
      : [],
    createdDate: role.createdDate.getTime(),
  };
};

export const buildSourceFileIndex = (file: SourceFile, indexProblems = true): SourceFileIndexEntry => {
  return {
    type: "SourceFileIndexEntry" as const,
    path: file.path,
    isDirectory: file.isDirectory,
    problems: indexProblems
      ? file.allProblems.map((problem: ProblemsWithReferenceNode) => {
          return {
            messages: getSnapshot(problem.messages),
            node: { type: problem.node.type, key: problem.node.path, apiIdentifier: problem.node.path },
            subNodeKey: problem.subNodeKey,
            nodeLabels: problem.nodeLabels,
          };
        })
      : [],
  };
};

export const buildGlobalActionIndex = (globalAction: GlobalAction): GlobalActionIndexEntry => {
  return {
    type: "GlobalActionIndexEntry" as const,
    ...pick(globalAction, ["key", "apiIdentifier"]),
    problems: globalAction.allProblems.map((problem: ProblemsWithReferenceNode) => {
      return {
        messages: getSnapshot(problem.messages),
        node: pick(problem.node, ["type", "apiIdentifier", "key"]),
        subNodeKey: problem.subNodeKey,
        nodeLabels: problem.nodeLabels,
      };
    }),
    createdDate: globalAction.createdDate.getTime(),
  };
};

export const buildConfigurationVariableIndex = (
  configValue: AllConfigurationVariables,
  indexProblems = true
): ConfigurationVariableIndexEntry => {
  return {
    ...pick(configValue, ["apiIdentifier", "key"]),
    ...(LegacyConfigurationVariable.is(configValue)
      ? {
          type: "ConfigurationVariableIndexEntry" as const,
          values: {
            Development: {
              isSecret: (configValue as LegacyConfigurationVariable).values.Development.isSecret,
              value: (configValue as LegacyConfigurationVariable).values.Development.value,
            },
            Production: {
              isSecret: (configValue as LegacyConfigurationVariable).values.Production.isSecret,
              value: (configValue as LegacyConfigurationVariable).values.Production.value,
            },
          },
        }
      : {
          type: "EnvironmentConfigurationVariableIndexEntry" as const,
          value: (configValue as ConfigurationVariable).value,
          isSecret: (configValue as ConfigurationVariable).isSecret,
        }),
    problems: indexProblems
      ? configValue.allProblems.map((problem: ProblemsWithReferenceNode) => {
          return {
            messages: getSnapshot(problem.messages),
            node: pick(problem.node, ["key", "type", "name"]),
            subNodeKey: problem.subNodeKey,
            nodeLabels: problem.nodeLabels,
          };
        })
      : [],
    createdDate: configValue.createdDate.getTime(),
  };
};

export const buildApiKeyIndex = (apiKey: AllApiKeys): ApiKeyIndexEntry => {
  return {
    ...pick(apiKey, ["key", "name"]),
    ...(LegacyApiKey.is(apiKey)
      ? {
          secretKeys: {
            Development: {
              secretKey: (apiKey as LegacyApiKey).secretKeys.Development.secretKey,
            },
            Production: {
              secretKey: (apiKey as LegacyApiKey).secretKeys.Production.secretKey,
            },
          },
          type: "ApiKeyIndexEntry",
        }
      : {
          secretKey: (apiKey as ApiKey).secretKey,
          type: "EnvironmentApiKeyIndexEntry",
        }),
    roles: apiKey.roles.map((role) => String(role)),
    problems: apiKey.allProblems.map((problem: ProblemsWithReferenceNode) => {
      return {
        messages: getSnapshot(problem.messages),
        node: pick(problem.node, ["key", "type", "name"]),
        subNodeKey: problem.subNodeKey,
        nodeLabels: problem.nodeLabels,
      };
    }),
    createdDate: apiKey.createdDate.getTime(),
  };
};

export const buildConnectionIndex = (connection: Instance<typeof Connection>): ConnectionIndexEntry => {
  return {
    type: "ConnectionIndexEntry" as const,
    ...pick(connection, ["key", "name"]),
    defaultApiIdentifier: connection.configuration.defaultApiIdentifier,
    problems: connection.allProblems.map((problem) => {
      return {
        messages: getSnapshot(problem.messages),
        node: pick(problem.node, ["type", "key", "name"]),
        subNodeKey: problem.subNodeKey,
        nodeLabels: problem.nodeLabels,
      };
    }),
    createdDate: connection.createdDate.getTime(),
  };
};

export const buildAuthenticationMethodsIndex = (
  authenticationMethod: Instance<typeof AuthenticationMethod>
): AuthenticationMethodIndexEntry => {
  return {
    type: "AuthenticationMethodIndexEntry" as const,
    ...pick(authenticationMethod, ["key", "name"]),
    problems: authenticationMethod.allProblems.map((problem) => {
      return {
        messages: getSnapshot(problem.messages),
        node: pick(problem.node, ["type", "key", "name"]),
        subNodeKey: problem.subNodeKey,
        nodeLabels: undefined,
      };
    }),
    createdDate: authenticationMethod.createdDate.getTime(),
  };
};

export const buildShopifyConnectionIndex = (connection: Instance<typeof Connection>): ShopifyConnectionIndexEntry => {
  const configuration = connection.configuration as Instance<typeof ShopifyConnectionConfig>;
  return {
    type: "ShopifyConnectionIndexEntry" as const,
    ...pick(connection, ["key", "name"]),
    problems: connection.allProblems.map((problem) => {
      return {
        messages: getSnapshot(problem.messages),
        node: pick(problem.node, ["type", "key", "name"]),
        subNodeKey: problem.subNodeKey,
        nodeLabels: problem.nodeLabels,
      };
    }),
    createdDate: connection.createdDate.getTime(),
    shopifyApiVersion: configuration.shopifyApiVersion,
    requestedScopes: configuration.requestedScopesAsArray,
    normalizedScopes: configuration.normalizedScopes,
    enabledModelKeys: configuration.enabledModelKeysAsArray,
    appConfigs: configuration.appConfigsAsArray.map(({ contentHash, key }) => ({ contentHash, key })),
  };
};
export const buildAppSettingsIndex = (appSettings: AppSettings): AppSettingsIndexEntry => {
  return {
    type: "AppSettingsIndexEntry" as const,
    problems: appSettings.problems.allProblemsWithReferenceNode(appSettings).map((problem) => {
      return {
        messages: getSnapshot(problem.messages),
        node: pick(problem.node, ["type", "key", "name"]),
        subNodeKey: problem.subNodeKey,
        nodeLabels: problem.nodeLabels,
      };
    }),
  };
};

const defaultProblemSources: IndexEntryKey[] = [
  "roles",
  "configurationVariables",
  "apiKeys",
  "files",
  "globalActions",
  "models",
  "connections",
  "authenticationMethods",
  "appSettings",
];

export const LiveEnvironmentIndex = types
  .compose(
    FilesystemIntrospector,
    gadgetModelType(
      // typename is shared with the server version
      "EnvironmentIndex",
      {
        // These fields will be populated by live tree using the views on `ServerEnvironmentIndex`
        server_models: types.frozen<ModelIndexEntry[]>(),
        server_files: types.frozen<SourceFileIndexEntry[]>(),
        server_roles: types.frozen<RoleIndexEntry[]>(),
        server_connections: types.frozen<ConnectionIndexEntry[]>(),
        server_authenticationMethods: types.frozen<AuthenticationMethodIndexEntry[]>(),
        server_shopifyConnection: types.maybe(types.frozen<ShopifyConnectionIndexEntry>()),
        server_fileReferences: types.frozen<{ sourceFile: string; reference: SourceFileReference }[]>(),
        server_globalActions: types.frozen<GlobalActionIndexEntry[]>(),
        server_configurationVariables: types.frozen<ConfigurationVariableIndexEntry[]>(),
        server_apiKeys: types.frozen<ApiKeyIndexEntry[]>(),
        server_publishHash: types.frozen<number>(),
        server_dependenciesHash: types.frozen<string>(),
      }
    )
  )
  .views((self) => {
    const root: Instance<typeof LiveRoot> = getRoot(self);
    // The client side index should reflect new nodes created client side before they have roundtripped to the server, and reflect deleted nodes before they've been deleted on the server for a responsive user experience. We union the client side and server side data, taking care to give the client side precedence and ignore any server side data that has been deleted.
    const clientServerIndexUnion = <Node extends IAnyModelOrUnionType, Entry extends Record<string, any>>(
      serverEntries: Entry[],
      clientMap: Instance<LiveTreeMap<Node>>,
      buildEntry: (node: Instance<Node>) => Entry,
      unionKey = "key",
      clientInstanceFilter: (node: Instance<Node>) => boolean = () => true
    ) => {
      const clientEntries = clientMap.currentInstances.filter(clientInstanceFilter).map(buildEntry);
      const aliveServerEntries = serverEntries?.filter((entry) => !clientMap.tombstones.includes(entry[unionKey])) ?? [];

      return unionBy(clientEntries, aliveServerEntries, unionKey);
    };

    return {
      get models(): ModelIndexEntry[] {
        return clientServerIndexUnion(self.server_models, root.models, (model) => buildModelIndex(model, true));
      },
      get modelsWithoutProblemsData(): ModelIndexEntry[] {
        return clientServerIndexUnion(self.server_models, root.models, (model) => buildModelIndex(model, false));
      },
      get files(): SourceFileIndexEntry[] {
        return clientServerIndexUnion<typeof SourceFile, SourceFileIndexEntry>(
          self.server_files,
          root.files,
          (file) => buildSourceFileIndex(file, true),
          "path"
        );
      },
      get filesWithoutProblems(): SourceFileIndexEntry[] {
        return clientServerIndexUnion<typeof SourceFile, SourceFileIndexEntry>(
          self.server_files,
          root.files,
          (file) => buildSourceFileIndex(file, false),
          "path"
        );
      },
      get roles(): RoleIndexEntry[] {
        return clientServerIndexUnion<typeof Role, RoleIndexEntry>(self.server_roles, root.roles, (role) => buildRoleIndex(role, true));
      },
      get rolesWithoutProblems(): RoleIndexEntry[] {
        return clientServerIndexUnion<typeof Role, RoleIndexEntry>(self.server_roles, root.roles, (role) => buildRoleIndex(role, false));
      },
      get connections(): ConnectionIndexEntry[] {
        return clientServerIndexUnion(self.server_connections, root.connections, buildConnectionIndex);
      },
      get authenticationMethods(): AuthenticationMethodIndexEntry[] {
        return clientServerIndexUnion(self.server_authenticationMethods, root.authenticationMethods, buildAuthenticationMethodsIndex);
      },
      get appSettings(): AppSettingsIndexEntry {
        return buildAppSettingsIndex(getAppSettings(self));
      },
      get shopifyConnection(): ShopifyConnectionIndexEntry | undefined {
        return head(
          clientServerIndexUnion(
            self.server_shopifyConnection ? [self.server_shopifyConnection] : [],
            root.connections,
            buildShopifyConnectionIndex,
            undefined,
            (connection) => connection.configuration.defaultApiIdentifier == "shopify"
          )
        );
      },
      get fileReferences(): { [sourceFile: string]: SourceFileReference[] } {
        const references = uniqWith(
          [
            ...root.models.currentInstances
              .flatMap((model) =>
                model.fileReferences.map(({ sourceFile, reference }) => ({ sourceFile, reference: { model: reference } }))
              )
              .reverse(),
            ...orderBy(root.globalActions.currentInstances, "order", "desc").flatMap((action) =>
              action.fileReferences.map(({ sourceFile, reference }) => ({ sourceFile, reference: { globalAction: reference } }))
            ),
            ...root.roles.currentInstances.flatMap((role) =>
              role.fileReferences.map(({ sourceFile, reference }) => ({ sourceFile, reference: { role: reference } }))
            ),
            ...self.server_fileReferences,
          ],
          isEqual
        );

        // Convert to a map to make it quick to lookup references by sourceFile
        const referenceMap: { [sourceFile: string]: SourceFileReference[] } = {};
        for (const { sourceFile, reference } of references) {
          if (!(sourceFile in referenceMap)) {
            referenceMap[sourceFile] = [];
          }

          referenceMap[sourceFile].push(reference);
        }

        return referenceMap;
      },
      get globalActions(): GlobalActionIndexEntry[] {
        return clientServerIndexUnion(self.server_globalActions, root.globalActions, buildGlobalActionIndex);
      },
      get configurationVariables(): ConfigurationVariableIndexEntry[] {
        return clientServerIndexUnion<typeof AllConfigurationVariableUnion, ConfigurationVariableIndexEntry>(
          self.server_configurationVariables,
          root.configurationVariables,
          (configurationVariable) => buildConfigurationVariableIndex(configurationVariable, true)
        );
      },
      get apiKeys(): ApiKeyIndexEntry[] {
        return clientServerIndexUnion<typeof AllApiKeysUnion, ApiKeyIndexEntry>(self.server_apiKeys, root.apiKeys, (apiKey) =>
          buildApiKeyIndex(apiKey)
        );
      },
      get configurationVariablesWithoutProblems(): ConfigurationVariableIndexEntry[] {
        return clientServerIndexUnion<typeof AllConfigurationVariableUnion, ConfigurationVariableIndexEntry>(
          self.server_configurationVariables,
          root.configurationVariables,
          (configurationVariable) => buildConfigurationVariableIndex(configurationVariable, false)
        );
      },
      get publishHash(): number {
        return self.server_publishHash;
      },
      get dependenciesHash(): string {
        return self.server_dependenciesHash;
      },
    };
  })

  .views((self) => {
    return {
      get modelErrors() {
        return self.models.flatMap((instance) =>
          instance.problems.filter((problem) => problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity)))
        );
      },
      get roleErrors() {
        return self.roles.flatMap((instance) =>
          instance.problems.filter((problem) => problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity)))
        );
      },
      get fileErrors() {
        return self.files.flatMap((instance) =>
          instance.problems.filter((problem) => problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity)))
        );
      },
      get globalActionErrors() {
        return self.globalActions.flatMap((instance) =>
          instance.problems.filter((problem) => problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity)))
        );
      },
      get configurationVariableErrors() {
        return self.configurationVariables.flatMap((instance) =>
          instance.problems.filter((problem) => problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity)))
        );
      },
      get apiKeyErrors() {
        return self.apiKeys.flatMap((instance) =>
          instance.problems.filter((problem) => problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity)))
        );
      },
      get connectionErrors() {
        return self.connections.flatMap((instance) =>
          instance.problems.filter((problem) => problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity)))
        );
      },
      get authenticationMethodErrors() {
        return self.authenticationMethods.flatMap((instance) =>
          instance.problems.filter((problem) => problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity)))
        );
      },
      get appSettingsErrors() {
        return self.appSettings.problems.filter((problem) =>
          problem.messages.some((message) => problemSeverityIsErrorOrAbove(message.severity))
        );
      },
    };
  })
  .views((self) => {
    return {
      get modelFatalErrors() {
        return self.modelErrors.filter((problem) => problem.messages.some((message) => message.severity == ProblemSeverity.Fatal));
      },
      get roleFatalErrors() {
        return self.roleErrors.filter((problem) => problem.messages.some((message) => message.severity == ProblemSeverity.Fatal));
      },
      get fileFatalErrors() {
        return self.fileErrors.filter((problem) => problem.messages.some((message) => message.severity == ProblemSeverity.Fatal));
      },
      get globalActionFatalErrors() {
        return self.globalActionErrors.filter((problem) => problem.messages.some((message) => message.severity == ProblemSeverity.Fatal));
      },
      get configurationVariableFatalErrors() {
        return self.configurationVariableErrors.filter((problem) =>
          problem.messages.some((message) => message.severity == ProblemSeverity.Fatal)
        );
      },
      get apiKeyFatalErrors() {
        return self.apiKeyErrors.filter((problem) => problem.messages.some((message) => message.severity == ProblemSeverity.Fatal));
      },
      get connectionFatalErrors() {
        return self.connectionErrors.filter((problem) => problem.messages.some((message) => message.severity == ProblemSeverity.Fatal));
      },
      get authenticationMethodFatalErrors() {
        return self.authenticationMethodErrors.filter((problem) =>
          problem.messages.some((message) => message.severity == ProblemSeverity.Fatal)
        );
      },
      get appSettingsFatalErrors() {
        return self.appSettingsErrors.filter((problem) => problem.messages.some((message) => message.severity == ProblemSeverity.Fatal));
      },
    };
  })
  .views((self) => ({
    get allFatalErrors() {
      return [
        ...self.modelFatalErrors,
        ...self.roleFatalErrors,
        ...self.fileFatalErrors,
        ...self.globalActionFatalErrors,
        ...self.configurationVariableFatalErrors,
        ...self.apiKeyFatalErrors,
        ...self.connectionFatalErrors,
        ...self.authenticationMethodFatalErrors,
        ...self.appSettingsFatalErrors,
      ];
    },
  }))
  .views((self) => ({
    get modelsHaveProblems() {
      return self.models.some((model) => model.problems.length > 0);
    },
    get filesHaveProblems() {
      return self.files.some((file) => file.problems.length > 0);
    },
    get rolesHaveProblems() {
      return self.roles.some((role) => role.problems.length > 0);
    },
    get globalActionsHaveProblems() {
      return self.globalActions.some((globalAction) => globalAction.problems.length > 0);
    },
    get configurationVariablesHaveProblems() {
      return self.configurationVariables.some((configurationVariable) => configurationVariable.problems.length > 0);
    },
    get apiKeysHaveProblems() {
      return self.apiKeys.some((apiKey) => apiKey.problems.length > 0);
    },
    get connectionsHaveProblems() {
      return self.connections.some((connection) => connection.problems.length > 0);
    },
    get authenticationMethodsHaveProblems() {
      return self.authenticationMethods.some((authenticationMethod) => authenticationMethod.problems.length > 0);
    },
    get appSettingsHaveProblems() {
      return self.appSettings.problems.length > 0;
    },
    get modelsHaveErrors() {
      return self.modelErrors.length > 0;
    },
    get filesHaveErrors() {
      return self.fileErrors.length > 0;
    },
    get rolesHaveErrors() {
      return self.roleErrors.length > 0;
    },
    get globalActionsHaveErrors() {
      return self.globalActionErrors.length > 0;
    },
    get configurationVariablesHaveErrors() {
      return self.configurationVariableErrors.length > 0;
    },
    get apiKeysHaveErrors() {
      return self.apiKeyErrors.length > 0;
    },
    get connectionsHaveErrors() {
      return self.connectionErrors.length > 0;
    },
    get authenticationMethodsHaveErrors() {
      return self.authenticationMethodErrors.length > 0;
    },
    get appSettingsHaveErrors() {
      return self.appSettingsErrors.length > 0;
    },
    get modelsHaveFatalErrors() {
      return self.modelFatalErrors.length > 0;
    },
    get filesHaveFatalErrors() {
      return self.fileFatalErrors.length > 0;
    },
    get rolesHaveFatalErrors() {
      return self.roleFatalErrors.length > 0;
    },
    get globalActionsHaveFatalErrors() {
      return self.globalActionFatalErrors.length > 0;
    },
    get configurationVariablesHaveFatalErrors() {
      return self.configurationVariableFatalErrors.length > 0;
    },
    get apiKeysHaveFatalErrors() {
      return self.apiKeyFatalErrors.length > 0;
    },
    get connectionsHaveFatalErrors() {
      return self.connectionFatalErrors.length > 0;
    },
    get appSettingsHaveFatalErrors() {
      return self.appSettingsFatalErrors.length > 0;
    },
  }))
  .views((self) => ({
    getRoleByKey(key: string) {
      return find(self.roles, { key });
    },
    getRoleByName(name: string) {
      return find(self.roles, { name });
    },
    getModelEntryByApiIdentifier(apiIdentifier: string) {
      return find(self.modelsWithoutProblemsData, { apiIdentifier });
    },
    getConfigurationVariableEntryByApiIdentifier(apiIdentifier: string) {
      return find(self.configurationVariables, { apiIdentifier });
    },
    getModelEntryByKey(key: string) {
      return find(self.modelsWithoutProblemsData, { key });
    },
  }))
  .views((self) => ({
    getModelEntryByFilePath(filePath: string) {
      const apiIdentifier = self.maybeGetModelIdByFilePath(filePath);
      if (!apiIdentifier) return;

      return self.getModelEntryByApiIdentifier(apiIdentifier);
    },
  }))
  .views((self) => ({
    getModelActionEntryByFilePath(filePath: string) {
      const modelApiIdentifier = self.maybeGetModelIdByFilePath(filePath);
      const actionApiIdentifier = self.maybeGetModelActionIdByFilePath(filePath);
      if (!modelApiIdentifier || !actionApiIdentifier) return;

      const model = self.getModelEntryByApiIdentifier(modelApiIdentifier);
      if (!model) return;

      const action = find(model.actions, { apiIdentifier: actionApiIdentifier });
      return action;
    },
    getAllProblems() {
      return defaultProblemSources.flatMap((key) => {
        const indexEntry = self[key];

        if (Array.isArray(indexEntry)) {
          return indexEntry.flatMap((entry) => entry.problems);
        } else {
          return indexEntry.problems;
        }
      });
    },
  }))
  .views((self) => ({
    getProblemsSummary() {
      const root = getRoot(self) as Instance<typeof LiveRoot>;
      const settings = getAppSettings(root);

      let errorCount = 0;
      let warningCount = 0;

      const summarizedProblems = self.getAllProblems().flatMap((problem) => {
        let tabType: WorkspacePageType | undefined = undefined;

        for (const message of problem.messages) {
          if (!tabType && message.tabType) {
            tabType = message.tabType;
          }

          if (problemSeverityIsErrorOrAbove(message.severity)) {
            errorCount++;
          } else if (message.severity == ProblemSeverity.Warning) {
            warningCount++;
          }
        }

        if (!tabType) return [];

        return summarizeProblem(problem, settings, tabType);
      });

      const groupedProblems = sortBy(Object.entries(groupBy(summarizedProblems, "groupKey")), [
        ([_key, problems]) => problems[0].tabPriority,
        ([_key, problems]) => problems[0].nodeNamePriority,
      ]).map(([groupKey, problems]) => {
        return {
          groupKey,
          problems,
        };
      });

      return { groupedProblems, errorCount, warningCount };
    },

    get hasProblems() {
      return (
        self.modelsHaveProblems ||
        self.filesHaveProblems ||
        self.rolesHaveProblems ||
        self.globalActionsHaveProblems ||
        self.configurationVariablesHaveProblems ||
        self.apiKeysHaveProblems ||
        self.connectionsHaveProblems ||
        self.authenticationMethodsHaveProblems ||
        self.appSettingsHaveProblems
      );
    },
    get hasErrors() {
      return (
        self.modelsHaveErrors ||
        self.filesHaveErrors ||
        self.rolesHaveErrors ||
        self.globalActionsHaveErrors ||
        self.configurationVariablesHaveErrors ||
        self.apiKeysHaveErrors ||
        self.connectionsHaveErrors ||
        self.authenticationMethodsHaveErrors ||
        self.appSettingsErrors
      );
    },

    get hasFatalErrors() {
      return !!self.allFatalErrors.length;
    },
  }))
  .views((self) => ({
    get errorCount() {
      return sumBy(
        [
          self.modelErrors,
          self.fileErrors,
          self.roleErrors,
          self.globalActionErrors,
          self.configurationVariableErrors,
          self.apiKeyErrors,
          self.connectionErrors,
          self.authenticationMethodErrors,
          self.appSettingsErrors,
        ],
        "length"
      );
    },
  }))
  .views((self) => ({
    getModelFieldByKey(modelKey: string, key: string) {
      const model: ModelIndexEntry | undefined = self.getModelEntryByKey(modelKey);
      if (!model) {
        return undefined;
      }
      return find(model.fields, { key });
    },
    get allowDeploy() {
      return !self.hasFatalErrors;
    },
  }));

/**
 * Special helper function to get the source file index.
 *
 * However, unlike the `getEnvironmentIndex` function, this one will work on a ReadOnlySchemaRoot, so that you can get a source file index from any roots.
 */
export const getFilesWithoutProblemsIndex = (node: IStateTreeNode): SourceFileIndexEntry[] => {
  const root: Instance<IAnyModelType> = getRoot(node);
  if (root.type === "ReadOnlySchemaRoot") {
    return root.getFilesWithoutProblemsIndex();
  } else {
    return getEnvironmentIndex(node).filesWithoutProblems;
  }
};
